import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteConfirmationModal from "../../../components/commonComponent/DeleteConfirmationModal";
import Pagination from "../../../components/commonComponent/Pagination";
import Search from "../../../components/commonComponent/Search";
import Table from "../../../components/commonComponent/Table";
import ViewModal from "../../../components/commonComponent/ViewModal";
import useModal from "../../../hooks/useModal";
import "../../../styles/SolarStation.css";
import { deleteData } from "../../../utils/utils";
import useLoader from "../../../hooks/useLoader";

const API_URL = process.env.REACT_APP_API_URL;
const ITEMS_PER_PAGE = 10;

const columns = [
  { header: "File", accessor: "file" },
  { header: "Xml Data", accessor: "xmlData" },
];

const SitemapPage = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const { loading, showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();

  const {
    showDeleteModal,
    showViewModal,
    selectedItem,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleOpenViewModal,
    handleCloseViewModal,
  } = useModal();

  // Fetch all sitemaps
  const fetchSitemaps = async () => {
    try {
      const response = await fetch(`${API_URL}/sitemap/getall`);
      const result = await response.json();
      setData(result.data || []);
      setFilteredData(result.data || []);
    } catch (err) {
      setError(err.message || "Failed to fetch sitemaps");
    }
  };

  useEffect(() => {
    fetchSitemaps();
  }, []);

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (value.trim().length > 0) {
      const filtered = data.filter((item) =>
        item.file.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(data);
    }
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteData(`${API_URL}/sitemap/sitemap`, selectedItem._id, setError);
      toast.success("Deleted successfully", { autoClose: 1000 });
      setData((prev) => prev.filter((item) => item._id !== selectedItem._id));
      setFilteredData((prev) => prev.filter((item) => item._id !== selectedItem._id));
    } catch (err) {
      toast.error("Error deleting data", { autoClose: 1000 });
    }
    handleCloseDeleteModal();
  };

  const handleGenerateSitemap = async (item) => {
    try {
      showLoader();

      const match = item.file.match(/^sitemap-(\w+)/);
      if (!match) {
        toast.error("Invalid sitemap file name format");
        return;
      }

      const type = match[1];

      const response = await fetch(`https://api.solarstation.in/sitemap/sitemap-${type}`, {
        method: "GET",
        headers: {
          file: item.file,
          xmlData: "application/xml",
        },
      });

      if (response.ok) {
        toast.success(`Sitemap generated for "${item.file}"`, { autoClose: 1500 });
        await fetchSitemaps(); // Refresh after generation
      } else {
        const result = await response.json();
        toast.error(result.error || "Failed to generate sitemap");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error generating sitemap");
    } finally {
      hideLoader();
    }
  };

  const fieldsToShow = {
    File: "file",
    XmlData: "xmlData",
  };

  const paginatedData = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div>
      <h2 className="capitalize font-bold text-[24px] mb-4">Sitemaps List</h2>

      <div className="flex justify-between items-center shadow-md px-8 py-[10px] rounded-md bg-[#fff] text-base mb-4">
        <Search
          searchFields={["file"]}
          onSearch={handleSearch}
          placeholder="Search by file name..."
        />

        <Link
          to="/sitemap/add"
          className="inline-flex items-center justify-center rounded-md border border-white px-4 py-1.5 text-center font-medium text-white bg-[#1C2434] hover:bg-opacity-90"
        >
          Add Sitemap
        </Link>
      </div>

      <div className="table-container">
        {paginatedData.length > 0 ? (
          <Table
            columns={columns}
            data={paginatedData}
            showIndex={true}
            currentPage={currentPage}
            itemsPerPage={ITEMS_PER_PAGE}
            onDelete={handleOpenDeleteModal}
            onUpdate={(item) => navigate(`/sitemap/edit/${item._id}`)}
            onView={handleOpenViewModal}
            actions={{
              showView: true,
              showDelete: true,
              customActions: [
                {
                  label: "Generate",
                  onClick: handleGenerateSitemap,
                  buttonClass: "bg-green-600 text-white hover:bg-green-700",
                },
              ],
            }}
            loading={loading}
          />
        ) : (
          <div className="text-center text-gray-500">No sitemaps found.</div>
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(filteredData.length / ITEMS_PER_PAGE)}
        onPageChange={handlePageChange}
      />

      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        onConfirm={handleDeleteConfirm}
      />

      <ViewModal
        show={showViewModal}
        onHide={handleCloseViewModal}
        data={selectedItem}
        fields={fieldsToShow}
      />
    </div>
  );
};

export default SitemapPage;
